import { trigger, state, style, transition, animate } from '@angular/animations';
import { formatNumber } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, HostListener, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { DialogConfirmComponent } from 'src/app/dialog/dialog-confirm/dialog-confirm.component';
import { ComponentCanDeactivate } from 'src/app/_guard/pending-changes.guard';
import { AppService, GRSSResponse } from 'src/app/_services/app.service';

@Component({
    selector: 'app-contribute',
    templateUrl: './contribute.component.html',
    styleUrls: ['./contribute.component.css'],
    animations: [
        trigger('slider', [
            state('*', style({
                height: 0
            })),
            state('show', style({
                height: '*'
            })),
            transition('* <=> show', [animate('400ms cubic-bezier(0.25, 0.8, 0.25, 1)')])
        ])
    ],
    standalone: false
})
export class ContributeComponent implements OnInit, OnDestroy, ComponentCanDeactivate, AfterViewInit {

  @ViewChild('spreadsheetInput') spreadsheetInput: ElementRef;

  contributeMarket: ContributeMarket;
  contributeMarketTemp: ContributeMarket;

  hasCurrentContributions: boolean = false;
  instrumentLength: number = 0;
  timeToOpenOrClose: string;
  displayColumnsAll: string[]  = ['name', 'space', 'assessedRate', 'assessedVolume', 'newRate', 'newVolume', 'comments'];
  displayColumns: string[]  = ['name', 'newRate', 'comments'];
  dataSourceNew;
  canEnablebtnSubmit: boolean = false;
  hasSubmitted: boolean = false;
  currentTimeColumn: string = '';
  intervals: any[] = [];

  shortdescription: string = '';
  
  local: string = 'en-US';
  discardChanges: boolean = false;
  
  uploadedFile: any;
  filename: string = '';
  hasUploadedAcceptedFile: Boolean = false;
  spreadsheetUpload: ContributionsFileUpload;
  
  allowSubmissions: boolean = false;
  isLoading: boolean = false;

  // tslint:disable-next-line: max-line-length
  constructor(private router: Router, @Inject(LOCALE_ID) private localeID: string, private translate: TranslateService, private dialog: MatDialog, private route: ActivatedRoute, private http: HttpClient, @Inject('BASE_URL') private baseUrl: string, public service: AppService) {
    this.service.SetTranslation('my-general-markets/contribute');
  }

  ngOnInit() {
    this.InitializeMarket();
  }

  ngOnDestroy() {
    if (this.intervals.length > 0) {
      this.intervals.forEach(interval => {
        clearInterval(interval);
      });
    }
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    // insert logic to check if there are pending changes here;
    // returning true will navigate without confirmation
    // returning false will show a confirm dialog before navigating away

    let hasChanges: boolean = false;

    if (JSON.stringify(this.contributeMarket) !== JSON.stringify(this.contributeMarketTemp) && this.discardChanges === false) {
      hasChanges = true;
    }

    return !hasChanges;
  }

  ngAfterViewInit() {
    this.route.queryParams.subscribe(params => {
      this.contributeMarket.marketId = params['ID'];
      this.CheckMarketType(this.contributeMarket.marketId).then(canProceed => {
        if (canProceed === true) {
          this.SetShortDescription();
          this.GetContributions();
          const interval = setInterval(() => {
            this.service.GetRemainingTime(this.contributeMarket.marketId, 'Contribution StartOrFinish').then(res => {
              this.timeToOpenOrClose = res.time;
              if (this.currentTimeColumn === '') {
                this.currentTimeColumn = res.timeColumn;
              } else if ((this.currentTimeColumn !== res.timeColumn) || res.time.split('0').join('').replace(/\:/gi, '').trim() === '') {
                if (this.currentTimeColumn === 'Contribution Start' && res.timeColumn === 'Contribution Finish') {
                  this.GetContributions();
                } else if ((this.currentTimeColumn === 'Contribution Finish' && res.timeColumn === 'Contribution Start') || (res.timeColumn === 'Contribution Finish' && this.allowSubmissions === false)) {
                  this.contributeMarket.isMarketOpen = false;
                  this.contributeMarketTemp.isMarketOpen = false;
                }
                this.currentTimeColumn = res.timeColumn;
              }
            });
          }, 1000);
          this.intervals.push(interval);
        } else {
          this.discardChanges = true;
          this.router.navigate(['contribute/contributeTransact'], { queryParams: {ID: this.contributeMarket.marketId} });
        }
      });
    });
    
  }

  /**
   * Gets and sets the contribution instructions description
   */
  SetShortDescription() {
    this.translate.get('Contribute.Messages.msgParaShortDes').subscribe((translated: string) => {
      this.http.get<CultureSettings>(this.baseUrl + 'api/contribute/getDecimalPlaces/' + this.contributeMarket.marketId + '/' + this.service.getLocalLanguage()).subscribe(result => {
        this.shortdescription = translated.replace(/\@rate/gi, result.decimalPlaces).replace('@culture', result.cultureName);
      }, error => {
        console.error(error);
      });
    });
  }

  /**
   *  Checks the market type
   * @param marketId Selected market ig
   */
  CheckMarketType(marketId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.service.currentUser.userContributeMarkets.find(e => e.marketId === marketId).isTransactional === false) {
        resolve(true);
      } else {
        resolve(false);
      }
    });
  }
  
  /**
   * Check if the window override enabled to allow bypass contribution window times and submit contributions
   */
  CheckOverrideWindow() {
    this.http.get<boolean>(this.baseUrl + 'api/contribute/checkWindowOpen/submission' + this.contributeMarket.marketId).subscribe(result => {
      this.allowSubmissions = result;
    }, error => {
      console.error(error);
    });
  }

  /** Initializes the market details */
  InitializeMarket() {
    this.spreadsheetUpload = {
      market: null,
      uploadedFile: '',
      fileExtension: ''
    };
    this.local = this.service.getLocalLanguage().substr(0, 2);
    this.contributeMarket = {
      marketId: '',
      marketCode: '',
      isMarketOpen: false,
      isAllowedUser: false,
      isTransactional: false,
      instruments: null
    };
  }

  /**Click event for the populate button */
  OnClickPopulate() {
    if (this.hasUploadedAcceptedFile === false) {
      if (this.filename === '') {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaEmptyFile');
      } else {
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaInvSpreadsheetFileType');
      }
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(this.uploadedFile);
      reader.onload = (event: any) => {
        const tempInstruments: ContributeMarketRecord[] = JSON.parse(JSON.stringify(this.contributeMarket.instruments));
        this.spreadsheetUpload.market = this.contributeMarket;
        this.spreadsheetUpload.uploadedFile = event.target.result;
        this.service.showSpinner();
        this.http.post<ContributionsFileUploadResponse>(this.baseUrl + 'api/contribute/populateRates', this.spreadsheetUpload).subscribe(result => {
          if (result.response.statusCode === 1) {
            this.contributeMarket.instruments = result.instruments;
            this.contributeMarket.instruments.forEach(o => {
              const old = tempInstruments.find(i => i.instrumentId === o.instrumentId);
              o.newRate = o.rate;
              o.newVolume = o.volume;

              o.rate = old.rate;
              o.volume = old.volume;
            });
            this.FormatNumbers(-1);
            this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
            this.EnablebtnSubmit();

            this.spreadsheetUpload = {
              market: null,
              uploadedFile: '',
              fileExtension: ''
            };
          } else if (result.response.statusCode === 4) {
            this.service.SaveStatus(true, 'Contribute.header', result.response.statusMessage);
          } else {
            this.service.SaveStatus(false, this.translate.instant('Contribute.header'), result.response.statusMessage, true);
          }
        }, error => {
          console.error(error);
        }).add(() => {
          this.service.hideSpinner();
          if (this.spreadsheetInput) {
            this.hasUploadedAcceptedFile = false;
            this.filename = '';
            this.spreadsheetInput.nativeElement.value = '';
            this.spreadsheetUpload = {
              market: null,
              uploadedFile: '',
              fileExtension: ''
            };
          }
        });
      };
      reader.onerror = (error) => {
        console.error('Error: ', error);
        this.service.SaveStatus(false, 'Contribute.header', 'Contribute.Messages.msgErrorUpldFile');
      };
    }
  }

  /** Fires when user changes the upload file */
  spreadsheetInputChange(fileInputEvent: any) {
    const acceptFileTypes: string[] = [ 'xls', 'xlsx' ];
    this.filename = fileInputEvent.target.files[0].name;
    this.hasUploadedAcceptedFile = false;
    if (this.filename) {
      const fileSplit = this.filename.split('.');
      if (acceptFileTypes.includes(fileSplit[fileSplit.length - 1])) {
        this.hasUploadedAcceptedFile = true;
        this.uploadedFile = fileInputEvent.target.files[0];
        this.spreadsheetUpload.fileExtension = fileSplit[fileSplit.length - 1];
      }
      if (this.filename.length > 40) {
        // this.filename = this.filename.substr(0, 30) + '____.' + fileSplit[1];
      }
    }

    if (this.hasUploadedAcceptedFile === false) {
      this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderInvFile', 'Contribute.Messages.msgParaInvSpreadsheetFileType');
    }
  }

  /**
   *  Fires when data bounds to the table
   * @param value value
   * @param decPlaces decimal places
   */
  OnDataBound(value: number, decPlaces: number): any {
    let currentVal = String(value);
    if (!isNaN(Number(currentVal)) && currentVal !== '') {
      let temp: string = currentVal;
      if (temp.includes('.')) {
        temp = temp.replace('-', '');
        const beforeDec = temp.split('.')[0].length.toString();
        currentVal = formatNumber(Number(currentVal), this.service.getLocalLanguage(), beforeDec + '.' + decPlaces + '-' + decPlaces); 
      }
    }
    return currentVal;
  }

  /** Gets the contributions for the market */
  GetContributions() {
    this.isLoading = true;
    this.service.showSpinner();
    this.http.get<ContributeMarket>(this.baseUrl + 'api/contribute/getContributions/' + this.contributeMarket.marketId).subscribe(result => {
      let initialAssign: boolean = false;
      if (!this.contributeMarket.instruments) {
        initialAssign = true;
      }

      this.contributeMarket = result;
      this.displayColumns = this.displayColumnsAll;

      let hasVolume: boolean = false;
      let hasAssessed: boolean = false;
      let hasCurrentRate: boolean = false;

      this.contributeMarket.instruments.forEach(i => {

       // i.comments = '';
        // if (initialAssign === true) {
          i.newRate = i.rate;
          i.newVolume = i.volume;
        // }

        if (i.calculationType === 'wav' && hasVolume === false) {
          hasVolume = true;
        }

        if (i.assessedRate && hasAssessed === false) {
          hasAssessed = true;
        }

        if (i.rate && hasCurrentRate === false) {
          hasCurrentRate = true;
          this.hasCurrentContributions = true;
        }

      });
      
      this.FormatNumbers(0);

      if (hasVolume === false) {
        const volumeItems: string[] = ['assessedVolume', 'currentVolume', 'newVolume'];
        this.displayColumns = this.displayColumns.filter(f => !volumeItems.includes(f));
      }

      if (hasAssessed === false) {
        const assessedItems: string[] = ['assessedRate', 'assessedVolume'];
        this.displayColumns = this.displayColumns.filter(f => !assessedItems.includes(f));
      }

      if (hasCurrentRate === false) {
        const currentItems: string[] = ['currentRate', 'currentVolume'];
        this.displayColumns = this.displayColumns.filter(f => !currentItems.includes(f));
      }

      this.contributeMarketTemp = JSON.parse(JSON.stringify(this.contributeMarket));

      this.instrumentLength = this.contributeMarket.instruments.length;
      this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
      
      if (initialAssign === true) {
        this.EnablebtnSubmit();
      }
    }, error => {
      console.error(error);
    }).add(() => {
      this.service.hideSpinner();
      this.isLoading = false;
      if (this.spreadsheetInput) {
        this.filename = '';
        this.spreadsheetInput.nativeElement.value = '';
        this.spreadsheetUpload = {
          market: null,
          uploadedFile: '',
          fileExtension: ''
        };
      }
    });
  }


  /** Formats the numbers according to the browser's culture */
  FormatNumbers(from: number) {

      this.contributeMarket.instruments.forEach(i => {
        if (i.decimalPlaces && i.newRate) {

          let temp = i.newRate.replace('-', '');
          let beforeDec = temp.split('.')[0].length.toString();
          i.newRate = formatNumber(Number(i.newRate), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
          if (i.level === 3 || i.level === 0) {
            i.assessedRate = formatNumber(Number(i.assessedRate), this.localeID, beforeDec + '.' + i.decimalPlaces + '-' + i.decimalPlaces);
          }

          if (from >= 0) {
            i.rate = i.newRate;
          }
        }
        i.spread = '0';
        i.defaultSpread = '0';
        

        if (i.previousRate) {
          const temp = i.previousRate.replace('-', '');
          const parts = temp.split('.');
          const beforeDec = parts[0].length.toString();
          const afterDec = parts[1] ? parts[1].length.toString() : '0';
          i.previousRate = formatNumber(Number(i.previousRate), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
        }
      // let temp = i.spread.replace('-', '');
      // let beforeDec = temp.split('.')[0].length.toString();
      // let afterDec = temp.split('.')[1].length.toString();
      // i.spread = formatNumber(Number(i.spread), this.localeID, beforeDec + '.' + afterDec + '-' + afterDec);
        // i.decimalPlacesStep = (Math.round(0 * 100) / 100).toFixed(i.decimalPlaces).toString().replace(/.$/, '1');

      });
  }

  /** Check requirements and enables the submit button */
  EnablebtnSubmit() {
    this.canEnablebtnSubmit = this.CheckInputs();
  }

  /** Checks the form inputs to enable submit button */
  CheckInputs(): boolean {
    let hasChanged: boolean = false;
    this.contributeMarket.instruments.forEach(i => {
      if (hasChanged === false) {
        if (i.calculationType === 'wav') {
          if ((i.newRate !== i.rate) || (i.newVolume !== i.volume)) {
            hasChanged = true;
          }
        } else {
          if (i.newRate !== i.rate) {
            hasChanged = true;
          }
        }
      }
    });

    return hasChanged;
  }

  /** Click event for the submit button */
  OnSubmitClick() {
    this.hasSubmitted = true;
    const tempInstruments: ContributeMarketRecord[] = JSON.parse(JSON.stringify(this.contributeMarket.instruments));
    this.contributeMarket.instruments.forEach(o => {
      o.rate = o.newRate;
      o.volume = o.newVolume;
    });
    this.http.post<VerifyResponse>(this.baseUrl + 'api/contribute/verifyRates', this.contributeMarket).subscribe(re => {
      if (re.response.statusCode === 1) {
        this.dialog.open(DialogConfirmComponent, {
          disableClose: true,
          data: {
            title: this.translate.instant('Contribute.Messages.msgHeaderCnfrm'),
            message: re.response.statusMessage + this.translate.instant('Contribute.Messages.msgParaCnfrm'),
            innerHTML: true
          },
          position: { top: '5%' }
        }).afterClosed().subscribe(res => {
          // Evaluate the users response.
          if (res === true) {

            this.service.showSpinner();
            this.http.post<GRSSResponse>(this.baseUrl + 'api/contribute/submitRates', this.contributeMarket).subscribe(result => {
              if (result.statusCode === 1) {
                this.hasSubmitted = false;
                this.GetContributions();
                this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderCont', result.statusMessage);
              } else if (result.statusCode === 4) {
                this.service.SaveStatus(true, 'Contribute.Messages.msgHeaderCont', result.statusMessage);
              } else {
                this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderCont', result.statusMessage);
              }
            }, error => {
              console.error(error);
            }).add(() => {
              this.service.hideSpinner();
            });
            
            this.contributeMarket.instruments.forEach(o => {
              o.newRate = o.rate;
              o.newVolume = o.volume;
            });
            this.contributeMarketTemp.instruments.forEach(o => {
              o.newRate = o.rate;
              o.newVolume = o.volume;
            });
          } else {
            this.contributeMarket.instruments = tempInstruments;
            this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
          }
        });
      } else if (re.response.statusCode === 4) {
        this.service.SaveStatus(true, 'Contribute.Messages.msgHeaderCont', re.response.statusMessage);
      } else {
        this.contributeMarket.instruments = tempInstruments;
        this.dataSourceNew = new MatTableDataSource(this.contributeMarket.instruments);
        this.service.SaveStatus(false, 'Contribute.Messages.msgHeaderCont', re.response.statusMessage);
      }
    }, error => {
      console.error(error);
    });
  }
}

/** An interface to access market  details */
interface ContributeMarket {
  marketId: string;
  marketCode: string;
  isMarketOpen: boolean;
  isAllowedUser: boolean;
  isTransactional: boolean;
  instruments: ContributeMarketRecord[];
}

/** An interface to access a record of a market contribution */
interface ContributeMarketRecord {
  submissionInstrumentId: string;
  instrumentId: string;
  instrumentDescription: string;
  level: number;
  defaultSpread: string;
  spread: string;
  rate?: string;
  volume?: number;
  comments: string;
  assessedRate?: string;
  assessedVolume?: number;
  previousRate?: string;
  previousVolume?: number;
  notes: string;
  status: string;
  calculationType: string;
  newRate: string;
  newVolume: number;
  decimalPlaces: number;
  decimalPlacesStep: string;
  instrumentAccepted: boolean;
}

/** Interface to access the culture settings response */
interface CultureSettings {
  decimalPlaces: string;
  cultureName: string;
}

/** Interface for access request for file upload */
interface ContributionsFileUpload {
  market: ContributeMarket;
  uploadedFile: string;
  fileExtension: string;
}

/** Interface to access the response from the file upload */
interface ContributionsFileUploadResponse {
  response: GRSSResponse;
  instruments: ContributeMarketRecord[];
}

/** Interface to access the response from the verify rates */
interface VerifyResponse {
  conflicted: boolean;
  response: GRSSResponse;
}

